/**
 * URL-as-Truth Workflow Store
 *
 * Key principle: URL is the single source of truth for navigation state.
 * The store derives its state from the URL and actions change the URL.
 *
 * This replaces the old workflowSteps.ts which had multiple sources of truth
 * (Inertia props, chat state, URL) requiring complex sync logic.
 */
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { router } from "@inertiajs/react";
import { Workflow, parseProjectUrl, projectUrl, campaignUrl } from "@shared";
import {
  getPageIndex,
  continueWorkflow,
  backWorkflow,
  type WorkflowPosition,
} from "@lib/workflowNavigation";

export type WorkflowContext = "launch" | "campaign";

const NO_MATCH = {
  projectUUID: null,
  campaignUUID: null,
  page: null,
  substep: null,
  workflowType: "launch" as WorkflowContext,
} as const;

function validateWorkflowUrl(
  step: string,
  substep: string | undefined
): { page: Workflow.WorkflowPage; substep: Workflow.SubstepName | null } | null {
  if (!(Workflow.WorkflowPages as readonly string[]).includes(step)) return null;

  const page = step as Workflow.WorkflowPage;
  const validSubsteps = Workflow.getSubstepOrder(page);

  if (validSubsteps.length === 0) return { page, substep: null };

  const resolved = substep && validSubsteps.includes(substep)
    ? (substep as Workflow.SubstepName)
    : (validSubsteps[0] as Workflow.SubstepName);

  return { page, substep: resolved };
}

/**
 * Parse URL to get current workflow state - this is the source of truth.
 * Handles both /projects/:uuid/:step/:substep (launch workflow) and
 * /campaigns/:uuid/:substep (campaign workflow). The two URL families
 * carry distinct UUIDs — parseUrl populates `projectUUID` XOR `campaignUUID`
 * depending on which family we're on. Consumers that specifically need a
 * campaign UUID read `campaignUUID`; ones that need a project UUID read
 * `projectUUID`. No more "this slot means X depending on context."
 */
export function parseUrl(): {
  projectUUID: string | null;
  campaignUUID: string | null;
  page: Workflow.WorkflowPage | null;
  substep: Workflow.SubstepName | null;
  workflowType: WorkflowContext;
} {
  const parsed = parseProjectUrl(window.location.pathname);
  if (!parsed) return { ...NO_MATCH };

  const validated = validateWorkflowUrl(parsed.step, parsed.substep);
  if (!validated) return { ...NO_MATCH };

  const workflowType = (parsed.workflowType as WorkflowContext) ?? "launch";
  return {
    projectUUID: workflowType === "launch" ? parsed.uuid : null,
    campaignUUID: workflowType === "campaign" ? parsed.uuid : null,
    ...validated,
    workflowType,
  };
}

/**
 * Launch surfaces (brainstorm, website) now live at /threads/:thread_id, which
 * carries no step in the path — so parseUrl() can't recover the workflow
 * position from the URL alone. The page instead tells us its position via the
 * Inertia component name + the project prop. This fallback seeds the store from
 * that when the URL doesn't parse. navigate() still builds /projects/:uuid/...
 * URLs; the server 302s those to the matching thread, so cross-step navigation
 * keeps working through the redirect bridge.
 */
export interface LaunchFallback {
  component: string | null;
  projectUUID: string | null;
}

function launchPositionFromComponent(
  component: string | null
): { page: Workflow.WorkflowPage; substep: Workflow.SubstepName | null } | null {
  if (component === "Brainstorm") return { page: "brainstorm" as Workflow.WorkflowPage, substep: null };
  if (component === "Website") {
    return { page: "website" as Workflow.WorkflowPage, substep: "build" as Workflow.SubstepName };
  }
  return null;
}

function resolveInitial(fallback?: LaunchFallback): ReturnType<typeof parseUrl> {
  const parsed = parseUrl();
  if (parsed.page !== null || !fallback?.projectUUID) return parsed;

  const pos = launchPositionFromComponent(fallback.component);
  if (!pos) return parsed;

  return {
    projectUUID: fallback.projectUUID,
    campaignUUID: null,
    page: pos.page,
    substep: pos.substep,
    workflowType: "launch",
  };
}

function buildUrl(
  page: Workflow.WorkflowPage,
  substep: Workflow.SubstepName | null,
  uuid: string,
  workflowType: WorkflowContext
): string {
  if (workflowType === "campaign") {
    // Campaign URLs are flat: /campaigns/:uuid/:substep.
    return substep ? campaignUrl(uuid, substep) : campaignUrl(uuid, "content");
  }
  return substep ? projectUrl(uuid, page, substep) : projectUrl(uuid, page);
}

// Re-export for convenience
export { getPageIndex };

export interface WorkflowState {
  /** Set when on /projects/:uuid/... URLs (launch workflow). */
  projectUUID: string | null;
  /** Set when on /campaigns/:uuid/... URLs (campaign workflow). */
  campaignUUID: string | null;
  page: Workflow.WorkflowPage | null;
  substep: Workflow.SubstepName | null;
  /** "launch" for /projects/:uuid/..., "campaign" for /campaigns/:uuid/... */
  workflowType: WorkflowContext;
  /** Per-campaign: has the user visited this campaign's review page? Keyed by campaignUUID. */
  hasVisitedReviewByCampaign: Record<string, boolean>;
  /** Per-campaign: section to scroll to when returning to review. Keyed by campaignUUID. */
  returnToSectionByCampaign: Record<string, string | null>;
}

export interface WorkflowActions {
  /** Sync state from current URL - called on mount and popstate. On thread
   *  URLs (which carry no step), pass the page's component+project fallback so
   *  the launch position can be recovered. */
  syncFromUrl: (fallback?: LaunchFallback) => void;

  /** Navigate to a specific page/substep. uuidOverride forces a specific
   *  resource UUID (project for launch trees, campaign for campaign trees)
   *  — mostly used internally; most callers omit and let the store pick. */
  navigate: (
    page: Workflow.WorkflowPage,
    substep?: Workflow.SubstepName | null,
    uuidOverride?: string
  ) => void;

  /** Continue to next step in workflow */
  continue: () => void;

  /** Go back to previous step in workflow */
  back: () => void;

  /** Navigate directly to the review page */
  returnToReview: () => void;

  /** Set the section to scroll to when returning to review */
  setReturnToSection: (section: string | null) => void;

  /** Clear the returnToSection state */
  clearReturnToSection: () => void;

  /** Set substep with optional returnToSection (convenience method) */
  setSubstep: (substep: Workflow.SubstepName, returnToSection?: string | null) => void;
}

export type WorkflowStore = WorkflowState & WorkflowActions;

// Pick the UUID that matches the current URL family. The store always has
// at most one set; this lookup makes the navigation code agnostic to which.
function activeUUID(state: { projectUUID: string | null; campaignUUID: string | null; workflowType: WorkflowContext }): string | null {
  return state.workflowType === "campaign" ? state.campaignUUID : state.projectUUID;
}

export const createWorkflowStore = (fallback?: LaunchFallback) => {
  const initial = resolveInitial(fallback);
  const initialCampaignKey = initial.campaignUUID ?? "";
  const hasVisitedReviewByCampaign: Record<string, boolean> =
    initial.substep === "review" && initialCampaignKey ? { [initialCampaignKey]: true } : {};

  return createStore<WorkflowStore>()(
    subscribeWithSelector((set, get) => ({
      ...initial,
      hasVisitedReviewByCampaign,
      returnToSectionByCampaign: {},

      syncFromUrl: (fallback?: LaunchFallback) => {
        const parsed = resolveInitial(fallback);
        const campaignKey = parsed.campaignUUID ?? "";
        const visited = { ...get().hasVisitedReviewByCampaign };
        if (parsed.substep === "review" && campaignKey) visited[campaignKey] = true;
        set({ ...parsed, hasVisitedReviewByCampaign: visited });
      },

      navigate: (page, substep = null, uuidOverride) => {
        const state = get();
        const uuid = uuidOverride ?? activeUUID(state);
        if (!uuid) {
          console.error(`Cannot navigate without a ${state.workflowType} UUID`);
          return;
        }

        const url = buildUrl(page, substep, uuid, state.workflowType);
        const visited = { ...state.hasVisitedReviewByCampaign };
        // hasVisitedReviewByCampaign is a campaign-only concept, so only key it
        // when we're navigating within the campaign tree.
        if (substep === "review" && state.workflowType === "campaign") {
          visited[uuid] = true;
        }

        // Cross-page navigation needs Inertia (component swap). Within a
        // page, pushState is enough since the React tree doesn't change.
        const needsInertia = page !== state.page;

        if (needsInertia) {
          if (visited !== state.hasVisitedReviewByCampaign) {
            set({ hasVisitedReviewByCampaign: visited });
          }
          router.visit(url);
        } else {
          window.history.pushState({}, "", url);
          set({
            page,
            substep,
            hasVisitedReviewByCampaign: visited,
          });
        }
      },

      continue: () => {
        const { page, substep } = get();
        // If the store lost its UUID (e.g. chat-driven pushState bypassed our
        // setter), re-parse from URL and seed.
        if (!activeUUID(get())) {
          const parsed = parseUrl();
          if (parsed.projectUUID || parsed.campaignUUID) {
            set({ projectUUID: parsed.projectUUID, campaignUUID: parsed.campaignUUID, workflowType: parsed.workflowType });
          }
        }

        if (!activeUUID(get())) {
          console.error("Cannot continue without a project or campaign UUID");
          return;
        }

        const current: WorkflowPosition = { page, substep };
        const next = continueWorkflow(current);

        // Don't navigate if we're at the end
        if (next.page === page && next.substep === substep) return;

        get().navigate(next.page!, next.substep);
      },

      back: () => {
        const { page, substep } = get();
        if (!activeUUID(get())) {
          console.error("Cannot go back without a project or campaign UUID");
          return;
        }

        const current: WorkflowPosition = { page, substep };
        const prev = backWorkflow(current);

        // Don't navigate if we're at the beginning
        if (prev.page === page && prev.substep === substep) return;

        get().navigate(prev.page!, prev.substep);
      },

      returnToReview: () => {
        const { campaignUUID } = get();
        if (!campaignUUID) {
          console.error("Cannot return to review without a campaign UUID");
          return;
        }
        get().navigate("ads", "review", campaignUUID);
      },

      setReturnToSection: (section) => {
        const { campaignUUID } = get();
        if (!campaignUUID) return;
        set({
          returnToSectionByCampaign: {
            ...get().returnToSectionByCampaign,
            [campaignUUID]: section,
          },
        });
      },

      clearReturnToSection: () => {
        const { campaignUUID } = get();
        if (!campaignUUID) return;
        const next = { ...get().returnToSectionByCampaign };
        delete next[campaignUUID];
        set({ returnToSectionByCampaign: next });
      },

      setSubstep: (substep, returnToSection) => {
        const { page, campaignUUID } = get();
        if (returnToSection !== undefined && campaignUUID) {
          set({
            returnToSectionByCampaign: {
              ...get().returnToSectionByCampaign,
              [campaignUUID]: returnToSection,
            },
          });
        }
        if (page && activeUUID(get())) {
          get().navigate(page, substep);
        }
      },
    }))
  );
};

// Computed selectors (not stored state)
export const selectCanGoBack = (s: WorkflowState): boolean => {
  const prev = backWorkflow({ page: s.page, substep: s.substep });
  return prev.page !== s.page || prev.substep !== s.substep;
};

export const selectCanGoForward = (s: WorkflowState): boolean => {
  const next = continueWorkflow({ page: s.page, substep: s.substep });
  return next.page !== s.page || next.substep !== s.substep;
};

export const selectPageNumber = (s: WorkflowState): number => getPageIndex(s.page);

export const selectPage = (s: WorkflowStore) => s.page;
export const selectSubstep = (s: WorkflowStore) => s.substep;
export const selectProjectUUID = (s: WorkflowStore) => s.projectUUID;
export const selectCampaignUUID = (s: WorkflowStore) => s.campaignUUID;
export const selectNavigate = (s: WorkflowStore) => s.navigate;
export const selectContinue = (s: WorkflowStore) => s.continue;
export const selectBack = (s: WorkflowStore) => s.back;
export const selectHasVisitedReview = (s: WorkflowStore): boolean => {
  const key = s.campaignUUID ?? "";
  return key ? Boolean(s.hasVisitedReviewByCampaign[key]) : false;
};
export const selectReturnToSection = (s: WorkflowStore): string | null => {
  const key = s.campaignUUID ?? "";
  return key ? (s.returnToSectionByCampaign[key] ?? null) : null;
};
export const selectWorkflowType = (s: WorkflowStore) => s.workflowType;
export const selectReturnToReview = (s: WorkflowStore) => s.returnToReview;
export const selectSetReturnToSection = (s: WorkflowStore) => s.setReturnToSection;
export const selectClearReturnToSection = (s: WorkflowStore) => s.clearReturnToSection;
export const selectSetSubstep = (s: WorkflowStore) => s.setSubstep;

// Computed selector: derive step from substep
export const selectStep = (s: WorkflowState): Workflow.AdsStepName | null => {
  return Workflow.deriveStep(s.substep);
};

// The workflow steps (pages) - static, doesn't depend on state
export const WORKFLOW_STEPS = Workflow.workflows.launch.steps;
export const selectPages = (_s: WorkflowState) => WORKFLOW_STEPS;
